import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { first } from 'lodash';
import { Disposer } from '../../custom_shared/misc';
import { NodeBaseModel } from '../models';
import { NodeIds } from '../misc';
import { NodeFactory } from '../factories';
import { NodeBaseDto } from '../types';
import { SourceStore } from '../../custom_shared/stores';
import SessionStore from './SessionStore';

export default class NodesStore extends Disposer {
    activeNodeId: string = '';

    nodes: NodeBaseModel[] = [];

    constructor(
        private readonly sessionStore: SessionStore,
        private readonly sourceStore: SourceStore
    ) {
        super();

        makeObservable(this, {
            activeNodeId: observable,
            nodes: observable,
            generalInformationNode: computed,
            createOrUpdateNodes: action.bound,
            setActiveNodeId: action.bound
        });

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (this.sessionStore.inProgress) {
                        return;
                    }

                    const data = this.sessionStore.response?.nodes ?? [];
                    this.createOrUpdateNodes(data);
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.nodes,
                () => {
                    if (this.activeNodeId && !this.nodes.some(t => t.id === this.activeNodeId)) {
                        this.setActiveNodeId('');
                    }

                    if (!this.activeNodeId && this.firstNode) {
                        this.setActiveNodeId(this.firstNode.id);
                    }
                }
            )
        );
    }

    get firstNode() {
        return first(this.nodes);
    }

    get generalInformationNode() {
        return this.nodes.find(node => node.id === NodeIds.GeneralInformation);
    }

    get limitsNode() {
        return this.nodes.find(node => node.id === NodeIds.Limits);
    }

    createOrUpdateNodes(nodes: NodeBaseDto[]) {
        this.nodes = NodeFactory.createOrUpdateNodes(nodes, this.nodes, this.sourceStore.sources);
    }

    setActiveNodeId(activeNodeId: string) {
        this.activeNodeId = activeNodeId;
    }

    getNodeByInputGuid(inputGuid: string) {
        return this.nodes.find(node => node.inputs.some(input => input.guid === inputGuid));
    }

    dispose() {
        this.disposeReactions();
    }
}
